import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PermissionStatusBadge, ClimateRiskBadge, VisibilityBadge } from "../components/Badges";
import { PERMISSION_STATUSES, VISIBILITY_LEVELS } from "../lib/constants";
import { Search, Filter, Sprout } from "lucide-react";

const ANY = "__any__";

export default function Collections() {
  const [filters, setFilters] = useState({ q: "", to_group: "", project: "", permission_status: "", dna_status: "", visibility: "" });
  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["collections", params],
    queryFn: async () => (await api.get(`/collections?${params}`)).data,
  });

  return (
    <div className="space-y-8" data-testid="collections-page">
      <header>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Collection records</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#2C2A28] mt-2">All seed collections</h1>
        <p className="text-[13.5px] text-[#5C5854] mt-2">Search and filter across every recorded provenance.</p>
      </header>

      <Card className="p-5 border-[#E6E2D8] shadow-sm bg-white">
        <div className="grid md:grid-cols-[1.6fr_1fr_1fr_1fr_1fr_1fr_auto] gap-3 items-end">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] mb-2 flex items-center gap-1">
              <Search className="w-3 h-3" /> Search
            </div>
            <Input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Species, common name, project, ID…"
              className="bg-white border-[#E6E2D8]"
              data-testid="filter-q"
            />
          </div>
          <FilterInput label="TO group" value={filters.to_group} onChange={(v) => setFilters((f) => ({ ...f, to_group: v }))} testid="filter-to" />
          <FilterInput label="Project" value={filters.project} onChange={(v) => setFilters((f) => ({ ...f, project: v }))} testid="filter-project" />
          <FilterSelect
            label="Permission"
            value={filters.permission_status}
            onChange={(v) => setFilters((f) => ({ ...f, permission_status: v }))}
            options={PERMISSION_STATUSES}
            testid="filter-perm"
          />
          <FilterSelect
            label="DNA"
            value={filters.dna_status}
            onChange={(v) => setFilters((f) => ({ ...f, dna_status: v }))}
            options={["yes", "no"]}
            testid="filter-dna"
          />
          <FilterSelect
            label="Visibility"
            value={filters.visibility}
            onChange={(v) => setFilters((f) => ({ ...f, visibility: v }))}
            options={VISIBILITY_LEVELS.map((v) => v.value)}
            testid="filter-visibility"
          />
          <Button
            variant="outline"
            onClick={() => setFilters({ q: "", to_group: "", project: "", permission_status: "", dna_status: "", visibility: "" })}
            className="border-[#E6E2D8] text-[#5C5854] hover:bg-[#F4F1EA] gap-2"
            data-testid="filter-reset"
          >
            <Filter className="w-3.5 h-3.5" /> Reset
          </Button>
        </div>
      </Card>

      <Card className="border-[#E6E2D8] shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F1EA] hover:bg-[#F4F1EA]">
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Collection</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Species</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">TO group</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Project</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Permission</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">DNA</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Climate risk</TableHead>
              <TableHead className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Visibility</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-[13px] text-[#8A857D]">Loading…</TableCell></TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-14 text-center">
                  <Sprout className="w-8 h-8 mx-auto text-[#8B5A2B]" strokeWidth={1.4} />
                  <div className="font-display font-medium text-[#2C2A28] mt-3">No collections match your filters.</div>
                  <p className="text-[12.5px] text-[#5C5854] mt-1">Try resetting filters or record a new collection.</p>
                </TableCell>
              </TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id} className="sk-row-hover cursor-pointer" data-testid={`row-${c.id}`}>
                <TableCell>
                  <Link to={`/collections/${c.id}`} className="block">
                    <div className="font-mono-data text-[11px] text-[#8A857D]">{c.collection_id}</div>
                    <div className="text-[12px] text-[#5C5854]">{c.date_collected || "—"}</div>
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/collections/${c.id}`} className="block">
                    <div className="italic font-serif-display text-[14px] text-[#2C2A28]">{c.species_scientific_name || "Unidentified"}</div>
                    <div className="text-[12px] text-[#5C5854]">{c.species_common_name}</div>
                  </Link>
                </TableCell>
                <TableCell><Link to={`/collections/${c.id}`}>{c.cultural?.traditional_owner_group || <span className="text-[#8A857D]">—</span>}</Link></TableCell>
                <TableCell><Link to={`/collections/${c.id}`}>{c.project_name || <span className="text-[#8A857D]">—</span>}</Link></TableCell>
                <TableCell><PermissionStatusBadge status={c.cultural?.cultural_permission_status} /></TableCell>
                <TableCell>
                  {c.genetic?.dna_sample_taken
                    ? <span className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#36453A]">Taken</span>
                    : <span className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#8A857D]">Not taken</span>}
                </TableCell>
                <TableCell><ClimateRiskBadge score={c.climate?.climate_risk_score} /></TableCell>
                <TableCell><VisibilityBadge level={c.visibility_level} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function FilterInput({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] mb-2">{label}</div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#E6E2D8]" data-testid={testid} />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] mb-2">{label}</div>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
        <SelectTrigger className="bg-white border-[#E6E2D8]" data-testid={testid}>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>All</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
